import type { Lens } from '@dhmk/zustand-lens';

import { ContactSteps } from '@/constants/solarEcomm/steps';
import type { MarketInfo } from '@/types/solarEcomm/marketInfo';
import type { OffsetKey, Products } from '@/types/solarEcomm/products';

export type SolarEcommState = {
  avgMonthlyBill: number | undefined;
  availableUtilities: Array<string>;
  selectedUtility: string;
  selectedBatteryOptionIndex: number | undefined;
  products: Products | undefined;
  selectedOffsetKey: OffsetKey | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  zipCode: string | undefined;
  state: string | undefined;
  ownsHome: boolean;
  currentStep: ContactSteps;
  maxOffset: number;
  marketInfo: MarketInfo | undefined;
  ecommReferrerPage: string | undefined;
};

export type SolarEcommActions = {
  setAvgMonthlyBill: (value: number | undefined) => void;
  setAvailableUtilities: (value: Array<string>) => void;
  setSelectedUtility: (value: string) => void;
  setSelectedBatteryOptionIndex: (value: number | undefined) => void;
  setProducts: (value: Products) => void;
  setSelectedOffsetKey: (value: OffsetKey) => void;
  setFirstName: (value: string | undefined) => void;
  setLastName: (value: string | undefined) => void;
  setPhone: (value: string | undefined) => void;
  setEmail: (value: string | undefined) => void;
  setZipCode: (value: string | undefined) => void;
  setState: (value: string | undefined) => void;
  setOwnsHome: (value: boolean) => void;
  setCurrentStep: (value: ContactSteps) => void;
  setMaxOffset: (value: number) => void;
  resetSolarEcommSlice: () => void;
  setMarketInfo: (value: MarketInfo | undefined) => void;
  setEcommReferrerPage: (value: string) => void;
};

export type SolarEcommSlice = SolarEcommState & { actions: SolarEcommActions };

export const solarEcommStateDefaultValues: SolarEcommState = {
  avgMonthlyBill: undefined,
  availableUtilities: [],
  selectedUtility: '',
  selectedBatteryOptionIndex: undefined,
  products: undefined,
  selectedOffsetKey: undefined,
  firstName: undefined,
  lastName: undefined,
  phone: undefined,
  email: undefined,
  zipCode: undefined,
  state: undefined,
  ownsHome: false,
  currentStep: ContactSteps?.CUSTOMIZE,
  maxOffset: 1,
  marketInfo: undefined,
  ecommReferrerPage: undefined,
};

const sliceName = 'solarEcomm';
export const createSolarEcommSlice: Lens<SolarEcommSlice> = (set) => ({
  ...solarEcommStateDefaultValues,
  actions: {
    setAvgMonthlyBill: (avgMonthlyBill) =>
      set({ avgMonthlyBill }, undefined, `${sliceName}/setAvgMonthlyBill`),
    setAvailableUtilities: (availableUtilities) =>
      set(
        { availableUtilities },
        undefined,
        `${sliceName}/setAvailableUtilities`
      ),
    setSelectedUtility: (selectedUtility) =>
      set({ selectedUtility }, undefined, `${sliceName}/setSelectedUtility`),
    setSelectedBatteryOptionIndex: (selectedBatteryOptionIndex) =>
      set(
        { selectedBatteryOptionIndex },
        undefined,
        `${sliceName}/setSelectedBatteryOptionIndex`
      ),
    setProducts: (products) =>
      set({ products }, undefined, `${sliceName}/setProducts`),
    setSelectedOffsetKey: (selectedOffsetKey) =>
      set(
        { selectedOffsetKey },
        undefined,
        `${sliceName}/setSelectedOffsetKey`
      ),
    setFirstName: (firstName) =>
      set({ firstName }, undefined, `${sliceName}/setFirstName`),
    setLastName: (lastName) =>
      set({ lastName }, undefined, `${sliceName}/setLastName`),
    setPhone: (phone) => set({ phone }, undefined, `${sliceName}/setPhone`),
    setEmail: (email) => set({ email }, undefined, `${sliceName}/setEmail`),
    setZipCode: (zipCode) =>
      set({ zipCode }, undefined, `${sliceName}/setZipCode`),
    setState: (state) => set({ state }, undefined, `${sliceName}/setState`),
    setOwnsHome: (ownsHome) =>
      set({ ownsHome }, undefined, `${sliceName}/setOwnsHome`),
    setCurrentStep: (currentStep) =>
      set({ currentStep }, undefined, `${sliceName}/setCurrentStep`),
    setMaxOffset: (maxOffset) =>
      set({ maxOffset }, undefined, `${sliceName}/setMaxOffset`),
    resetSolarEcommSlice: () =>
      set(
        solarEcommStateDefaultValues,
        undefined,
        `${sliceName}/resetSolarEcommSlice `
      ),
    setMarketInfo: (marketInfo) =>
      set({ marketInfo }, undefined, `${sliceName}/setMarketInfo`),
    setEcommReferrerPage: (ecommReferrerPage) =>
      set(
        { ecommReferrerPage },
        undefined,
        `${sliceName}/setEcommReferrerPage`
      ),
  },
});
